import { useEffect, useState } from 'react';
import { Box, Divider, Flex, Menu, Modal, Stack, Text } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { Button } from 'src/design-system/Button';
import type { TemplateWithoutSections } from 'src/data-capture/hooks/templates/useTemplates';
import type { FamilyMeeting } from 'src/meetings/hooks/useScopedMeetings';
import type { MeetingScope } from 'src/meetings/scope';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { AttendeeRow } from './AttendeeRow';
import { type Attendee, useMeetingAttendees } from './useMeetingAttendees';

type Props = {
  opened: boolean;
  onClose: () => void;
  scope: MeetingScope;
  meeting: FamilyMeeting;
  templates: TemplateWithoutSections[];
  isGenerating: boolean;
  onConfirm: (templateId: string, clientIds: string[]) => Promise<void>;
};

export function GenerateMeetingAgendaModal({
  opened,
  onClose,
  scope,
  meeting,
  templates,
  isGenerating,
  onConfirm,
}: Props) {
  const { attendees } = useMeetingAttendees(scope, meeting);

  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  // For an agenda the whole household is offered as attending by default, even if
  // they aren't on the calendar invite. Ticking someone only includes them in the
  // agenda — it never changes who is on the meeting.
  const isChecked = (attendee: Attendee) =>
    overrides[attendee.id] ?? (attendee.group === 'guest' || attendee.defaultChecked);
  const toggle = (attendee: Attendee) =>
    setOverrides(previous => ({ ...previous, [attendee.id]: !isChecked(attendee) }));

  const defaultTemplate = templates.find(template => template.isDefault) ?? templates[0];
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const selectedTemplate = templates.find(t => t.id === selectedTemplateId) ?? defaultTemplate;

  // The agenda is built from the ticked clients; advisers and external guests are
  // shown for context but don't contribute to the agenda content.
  const selectedClientIds = attendees.flatMap(attendee =>
    isChecked(attendee) && attendee.addBody.participantType === 'CLIENT'
      ? [attendee.addBody.clientId]
      : [],
  );

  // The modal stays mounted, so clear local selection once it closes to avoid
  // stale checkboxes and a stale template choice on the next open.
  useEffect(() => {
    if (opened) return;
    setOverrides({});
    setSelectedTemplateId(null);
  }, [opened]);

  const handleGenerate = async () => {
    if (!selectedTemplate || selectedClientIds.length === 0) return;
    await onConfirm(selectedTemplate.id, selectedClientIds);
  };

  const isBusy = isGenerating;

  return (
    <Modal opened={opened} onClose={onClose} title="Who is this meeting about?" size={440}>
      <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.10">
        Attendees
      </Text>
      <Stack gap="xxxs" mt="xs">
        {attendees.map(attendee => (
          <AttendeeRow
            key={attendee.id}
            name={attendee.name}
            role={attendee.role}
            checked={isChecked(attendee)}
            onToggle={() => toggle(attendee)}
            ariaLabel={`${attendee.name} attending`}
            isExternalGuest={attendee.isExternalGuest}
          />
        ))}
      </Stack>

      <Divider color="gray.1" mt="md" />

      <Text mt="md" size="textSm" fw={FONT_WEIGHTS.medium} c="gray.6">
        Choose meeting agenda template
      </Text>

      {templates.length === 0 ? (
        <Text mt="xxs" size="textSm" fw={FONT_WEIGHTS.medium} c="gray.6">
          No agenda templates are available. Create one to generate an agenda.
        </Text>
      ) : (
        <Menu
          position="bottom-start"
          width="target"
          styles={{ itemLabel: { minWidth: 0, overflowWrap: 'anywhere' } }}
        >
          <Menu.Target>
            <Flex
              mt="xxs"
              justify="space-between"
              align="center"
              gap="sm"
              py="xs"
              px="sm"
              style={{
                border: '1px solid var(--mantine-color-gray-1)',
                borderRadius: 16,
                cursor: 'pointer',
              }}
            >
              <Box style={{ minWidth: 0 }}>
                <Text
                  size="textSm"
                  fw={FONT_WEIGHTS.medium}
                  c="gray.10"
                  style={{ overflowWrap: 'anywhere' }}
                >
                  {selectedTemplate?.displayName ?? 'Select a template'}
                </Text>
                <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.6" mt={4}>
                  Meeting agenda
                </Text>
              </Box>
              <IconChevronDown
                size={24}
                color="var(--mantine-color-gray-10)"
                style={{ flexShrink: 0 }}
              />
            </Flex>
          </Menu.Target>
          <Menu.Dropdown>
            {templates.map(template => (
              <Menu.Item key={template.id} onClick={() => setSelectedTemplateId(template.id)}>
                {template.displayName}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      )}

      <Box mt="lg">
        <Button
          fullWidth
          onClick={handleGenerate}
          loading={isBusy}
          disabled={!selectedTemplate || isBusy || selectedClientIds.length === 0}
        >
          Generate for the selected attendees
        </Button>
      </Box>
    </Modal>
  );
}
